export interface InventoryItem {
  id: string;
  company_id: string;
  slug: string;
  name: string;
  sku: string;
  unit_of_measure: string;
  current_stock: number;
  minimum_stock: number;
  purchase_price: number;
  reference_price: number;
  description?: string | null;
  es_material_cliente: boolean;
  is_active: boolean;
  is_low_stock: boolean;
  station_requirements_count: number;
  created_at: string;
  updated_at: string;
}

export interface StationSupplyRequirement {
  id: string;
  company_id: string;
  slug: string;
  station_name: string;
  inventory_id: string;
  estimated_quantity: number;
  notes?: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  inventoryItem: InventoryItem | null;
}

export interface InventoryMovement {
  id: string;
  company_id: string;
  slug: string;
  inventory_id: string;
  movement_type: "restock" | "consumption" | "adjustment";
  quantity: number;
  previous_stock: number;
  new_stock: number;
  unit_cost?: number | null;
  station_name?: string | null;
  order_reference?: string | null;
  order_id?: string | null;
  waste: number;
  notes?: string | null;
  created_by_user_id?: string | null;
  created_at: string;
  updated_at: string;
  inventoryItem: InventoryItem | null;
}

export interface ReplenishmentRecommendation extends InventoryItem {
  quantity_to_minimum: number;
  suggested_restock_quantity: number;
  estimated_restock_cost: number;
  coverage_ratio: number | null;
  alert_level: "healthy" | "medium" | "high" | "critical";
}

export interface ReplenishmentDashboard {
  summary: {
    total_items: number;
    low_stock_items: number;
    out_of_stock_items: number;
    urgent_items: number;
    estimated_reorder_cost: number;
  };
  recommendations: ReplenishmentRecommendation[];
  recent_movements: InventoryMovement[];
  alert_history: InventoryAlertHistory[];
}

export interface InventoryAlertHistory {
  id: string;
  company_id: string;
  slug: string;
  alert_type: string;
  alert_status: "sent" | "failed";
  recipients: string[];
  low_stock_items_count: number;
  critical_items_count: number;
  estimated_reorder_cost: number;
  items_snapshot: Array<{
    id: string;
    name: string;
    sku: string;
    unit_of_measure: string;
    current_stock: number;
    minimum_stock: number;
    suggested_restock_quantity: number;
    estimated_restock_cost: number;
    alert_level: "healthy" | "medium" | "high" | "critical";
  }>;
  error_message?: string | null;
  triggered_at: string;
  created_at: string;
  updated_at: string;
}

export interface CreateInventoryItemRequest {
  slug: string;
  company_id: string;
  name: string;
  sku: string;
  unit_of_measure: string;
  current_stock: number;
  minimum_stock: number;
  purchase_price: number;
  reference_price: number;
  description?: string;
  es_material_cliente?: boolean;
}

export interface UpdateInventoryItemRequest {
  slug: string;
  company_id: string;
  name?: string;
  sku?: string;
  unit_of_measure?: string;
  current_stock?: number;
  minimum_stock?: number;
  purchase_price?: number;
  reference_price?: number;
  description?: string;
  es_material_cliente?: boolean;
  is_active?: boolean;
}

export interface CreateStationSupplyRequirementRequest {
  slug: string;
  company_id: string;
  station_name: string;
  inventory_id: string;
  estimated_quantity: number;
  notes?: string;
}

export interface UpdateStationSupplyRequirementRequest {
  slug: string;
  company_id: string;
  station_name?: string;
  inventory_id?: string;
  estimated_quantity?: number;
  notes?: string;
  is_active?: boolean;
}

export interface CreateInventoryMovementRequest {
  slug: string;
  company_id: string;
  inventory_id: string;
  movement_type: "restock" | "adjustment";
  quantity: number;
  unit_cost?: number;
  notes?: string;
  user_id?: string;
}

export interface ConsumedItem {
  inventory_id: string;
  quantity: number;
  waste: number;
}

export interface ConsumeStationSuppliesRequest {
  slug: string;
  company_id: string;
  station_name?: string;
  produced_units?: number;
  order_reference?: string;
  order_id?: string;
  notes?: string;
  user_id?: string;
  consumed_items?: ConsumedItem[];
}

export interface ValidateStockRequest {
  slug: string;
  company_id: string;
  station_name: string;
  produced_units: number;
}

export interface ValidateStockResponse {
  is_available: boolean;
  missing_items: Array<{
    inventory_id: string;
    material_name: string;
    sku: string;
    unit_of_measure: string;
    required_quantity: number;
    available_stock: number;
    missing_quantity: number;
  }>;
}

export const INVENTORY_UNITS = [
  "unidad",
  "m",
  "cm",
  "mm",
  "kg",
  "g",
  "litro",
  "ml",
  "rollo",
  "caja",
  "paquete",
  "hoja",
  "set",
] as const;

export type InventoryUnit = (typeof INVENTORY_UNITS)[number];
